import React, { useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ACCEPT_CERTIFICATE = '.pdf,.jpg,.jpeg,.png,.doc,.docx';
const ACCEPT_PASSPORT = '.pdf,.jpg,.jpeg,.png,.doc,.docx';
const ACCEPT_PHOTO = '.jpg,.jpeg,.png';

const CoachDocumentsSection = ({
  photoFileName,
  onPhotoChange,
  existingCertificates = [],
  newCertificateFiles = [],
  onAddCertificates,
  onRemoveExistingCertificate,
  onRemoveNewCertificate,
  existingPassport,
  passportFileName,
  onPassportChange,
  errors = {},
}) => {
  const photoInputRef = useRef(null);
  const certificateInputRef = useRef(null);
  const passportInputRef = useRef(null);

  const handleCertificateSelect = (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length) onAddCertificates(files);
    event.target.value = '';
  };

  const passportLabel = passportFileName || existingPassport?.fileName;

  return (
    <div className="space-y-6 pt-2">
      <div>
        <label className="text-sm font-medium text-foreground mb-2 block">Coach Photo</label>
        <div className="flex flex-wrap items-center gap-3">
          <Button
            type="button"
            variant="outline"
            size="sm"
            iconName="Upload"
            onClick={() => photoInputRef.current?.click()}
          >
            Choose File
          </Button>
          <span className="text-sm text-muted-foreground">{photoFileName || 'No file chosen'}</span>
        </div>
        <input
          ref={photoInputRef}
          type="file"
          accept={ACCEPT_PHOTO}
          className="hidden"
          onChange={(e) => onPhotoChange(e.target.files?.[0] || null)}
        />
      </div>

      <div>
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
          <label className="text-sm font-medium text-foreground">Coaching Certificates</label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            iconName="Plus"
            onClick={() => certificateInputRef.current?.click()}
          >
            Add Certificate
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mb-3">
          Upload one or more coaching certificates (PDF, Word, or image). At least one certificate is required.
        </p>

        {(existingCertificates.length > 0 || newCertificateFiles.length > 0) && (
          <div className="space-y-2 mb-3">
            {existingCertificates.map((certificate) => (
              <div
                key={certificate.id}
                className="flex items-center justify-between gap-3 rounded-lg border border-border bg-muted/20 px-3 py-2"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <Icon name="FileText" size={14} className="text-primary shrink-0" />
                  <span className="text-sm text-foreground truncate">{certificate.fileName}</span>
                  <span className="text-xs text-muted-foreground shrink-0">Saved</span>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {certificate.fileUrl && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-8 px-2"
                      onClick={() => window.open(certificate.fileUrl, '_blank')}
                    >
                      View
                    </Button>
                  )}
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0 text-destructive"
                    onClick={() => onRemoveExistingCertificate(certificate.id)}
                    title="Remove certificate"
                  >
                    <Icon name="X" size={14} />
                  </Button>
                </div>
              </div>
            ))}

            {newCertificateFiles.map((file, index) => (
              <div
                key={`${file.name}-${file.lastModified}-${index}`}
                className="flex items-center justify-between gap-3 rounded-lg border border-border bg-muted/20 px-3 py-2"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <Icon name="FileText" size={14} className="text-primary shrink-0" />
                  <span className="text-sm text-foreground truncate">{file.name}</span>
                  <span className="text-xs text-muted-foreground shrink-0">New</span>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 text-destructive"
                  onClick={() => onRemoveNewCertificate(index)}
                  title="Remove certificate"
                >
                  <Icon name="X" size={14} />
                </Button>
              </div>
            ))}
          </div>
        )}

        {errors.certificateFile && (
          <p className="text-sm text-destructive mb-2">{errors.certificateFile}</p>
        )}

        <input
          ref={certificateInputRef}
          type="file"
          accept={ACCEPT_CERTIFICATE}
          multiple
          className="hidden"
          onChange={handleCertificateSelect}
        />
      </div>

      <div>
        <label className="text-sm font-medium text-foreground mb-2 block">Passport / ID Document</label>
        <div className="flex flex-wrap items-center gap-3">
          <Button
            type="button"
            variant="outline"
            size="sm"
            iconName="Upload"
            onClick={() => passportInputRef.current?.click()}
          >
            Choose File
          </Button>
          <span className="text-sm text-muted-foreground">{passportLabel || 'No file chosen'}</span>
          {existingPassport?.fileUrl && !passportFileName && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => window.open(existingPassport.fileUrl, '_blank')}
            >
              View Current
            </Button>
          )}
        </div>
        <p className="text-xs text-muted-foreground mt-2">
          Optional passport or national ID copy (PDF, Word, or image).
        </p>
        {errors.passportFile && (
          <p className="text-sm text-destructive mt-2">{errors.passportFile}</p>
        )}
        <input
          ref={passportInputRef}
          type="file"
          accept={ACCEPT_PASSPORT}
          className="hidden"
          onChange={(e) => onPassportChange(e.target.files?.[0] || null)}
        />
      </div>
    </div>
  );
};

export default CoachDocumentsSection;
